import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FEATURE_ORDER, type FeatureKey } from '../lib/types'
import { loadCatalog, parseFiltersFromSearch, filtersToSearch, type Filters, computeActiveIndices, wouldButtonActivateAnything, testGitHubAccess } from '../lib/data'
import type { LoadedData } from '../lib/data'

// New type for tracking pending launches - use arrays instead of Sets for React state
type PendingLaunches = Partial<Record<FeatureKey, string[]>>

export default function IndexPage() {
  const nav = useNavigate()
  const { search } = useLocation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<LoadedData | null>(null)
  const [githubStatus, setGithubStatus] = useState<{
    accessible: boolean
    status?: number
    error?: string
    source?: 'github' | 'local' | 'neither'
    config?: {
      tokenConfigured: boolean
      localDisabled: boolean
    }
  } | null>(null)
  const [filters, setFilters] = useState<Filters>(() => parseFiltersFromSearch(search))
  
  // New state for pending launches (red buttons) - using arrays instead of Sets
  const [pendingLaunches, setPendingLaunches] = useState<PendingLaunches>({})
  
  // New state for hiding legacy entries
  const [hideLegacy, setHideLegacy] = useState<boolean>(false)

  useEffect(() => {
    setFilters(parseFiltersFromSearch(search))
  }, [search])

  useEffect(() => {
    // Test GitHub access first
    testGitHubAccess().then(setGithubStatus)
    
    // Then try to load the catalog
    loadCatalog()
      .then(setData)
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [])

  const activeIndices = useMemo(() => {
    if (!data) return []
    
    // First compute active indices based on filters
    let indices = computeActiveIndices(data, filters)
    
    // Then filter out legacy/scratchpad/tmp entries if hideLegacy is enabled
    if (hideLegacy) {
      indices = indices.filter(idx => {
        const path = data.all[idx].path
        // Case-sensitive check for legacy, scratchpad, or tmp in the path
        return !path.includes('legacy') && !path.includes('scratchpad') && !path.includes('tmp')
      })
    }
    
    return indices
  }, [data, filters, hideLegacy])

  const activeRuns = useMemo(() => {
    if (!data) return 0
    let cnt = 0
    for (const idx of activeIndices) {
      const p = data.all[idx].path
      if (data.runs.some(r => r.path === p)) cnt++
    }
    return cnt
  }, [data, activeIndices])

  const activeArtifacts = useMemo(() => {
    if (!data) return 0
    let cnt = 0
    for (const idx of activeIndices) {
      const p = data.all[idx].path
      if (data.artifacts.some(a => a.path === p)) cnt++
    }
    return cnt
  }, [data, activeIndices])

  // Check if any pending launches are selected
  const hasPendingLaunches = useMemo(() => {
    return Object.values(pendingLaunches).some(arr => arr && arr.length > 0)
  }, [pendingLaunches])

  // Check if a combination requires GPU (blocks launch)
  const requiresGPU = (feature: FeatureKey, value: string): boolean => {
    // Block LocalModel policies
    if (feature === 'Model' && value.toLowerCase().includes('local')) {
      return true
    }
    
    // Block WorldInTheLoop if it would use LocalModel forecaster
    if (feature === 'Evaluation Strategy' && value === 'WorldInTheLoop') {
      // Without additional config, we can't determine if it needs GPU
      // For safety, we'll allow it but warn in the config page
      return false
    }
    
    return false
  }

  // Check if current selection has GPU requirements
  const hasGPURequirements = useMemo(() => {
    for (const [feature, values] of Object.entries(pendingLaunches)) {
      if (!values || !Array.isArray(values)) continue
      for (const value of values) {
        if (requiresGPU(feature as FeatureKey, value)) {
          return true
        }
      }
    }
    return false
  }, [pendingLaunches])

  // Calculate estimated number of runs
  const estimatedRunCount = useMemo(() => {
    if (!data) return 0
    
    // Combine active filters and pending launches
    const combined: Partial<Record<FeatureKey, Set<string>>> = {}
    
    // For each feature (excluding Result Analyzer), check if there's any selection
    for (const feature of FEATURE_ORDER) {
      // Skip Result Analyzer - it doesn't count toward run count
      if (feature === 'Result Analyzer') continue
      
      const activeFilters = filters[feature] || new Set()
      const pendingValues = pendingLaunches[feature] || []
      
      if (activeFilters.size > 0 || pendingValues.length > 0) {
        // Has some selection - use those values
        combined[feature] = new Set([...activeFilters, ...pendingValues])
      } else {
        // No selection at all - use ALL values for this feature
        const allValues = data.featureValues[feature]?.map(v => v.value) || []
        if (allValues.length > 0) {
          combined[feature] = new Set(allValues)
        }
      }
    }
    
    // Calculate product (only the 5 features that matter for run count)
    const counts = Object.values(combined).map(set => set?.size || 1)
    if (counts.length === 0) return 0
    return counts.reduce((a, b) => a * b, 1)
  }, [filters, pendingLaunches, data])

  const toggle = (fk: FeatureKey, val: string) => {
    console.log(`[IndexPage.toggle] Toggling ${fk}: ${val}`)
    const set = new Set<string>(filters[fk] || [])
    const wasPressed = set.has(val)
    if (wasPressed) {
      set.delete(val)
      console.log(`[IndexPage.toggle] Removing ${val} from ${fk}`)
    } else {
      set.add(val)
      console.log(`[IndexPage.toggle] Adding ${val} to ${fk}`)
    }
    const next: Filters = { ...filters, [fk]: set }
    if (set.size === 0) delete next[fk]
    console.log('[IndexPage.toggle] New filters:', next)
    const searchString = filtersToSearch(next)
    console.log('[IndexPage.toggle] Navigating to:', { pathname: '/', search: searchString })
    nav({ pathname: '/', search: searchString })
  }

  const togglePending = (fk: FeatureKey, val: string) => {
    console.log(`[IndexPage.togglePending] Toggling pending ${fk}: ${val}`)
    setPendingLaunches(prev => {
      const next = { ...prev }
      const current = next[fk] || []
      
      if (current.includes(val)) {
        next[fk] = current.filter(v => v !== val)
        if (next[fk]!.length === 0) delete next[fk]
      } else {
        next[fk] = [...current, val]
      }
      
      return next
    })
  }

  const handleLaunchRuns = () => {
    console.log('[IndexPage] handleLaunchRuns called')
    console.log('[IndexPage] Current filters:', filters)
    console.log('[IndexPage] Current pendingLaunches:', pendingLaunches)
    
    // Check if there are any selections
    const hasActiveFilters = Object.keys(filters).length > 0
    const hasPending = Object.keys(pendingLaunches).length > 0
    
    console.log('[IndexPage] hasActiveFilters:', hasActiveFilters, 'hasPending:', hasPending)
    
    if (!hasActiveFilters && !hasPending) {
      alert('Please select at least one configuration to launch')
      return
    }
    
    // Convert pendingLaunches arrays to Sets for LaunchPage compatibility
    const pendingLaunchesAsSets = Object.fromEntries(
      Object.entries(pendingLaunches).map(([key, values]) => [key, new Set(values)])
    )
    
    console.log('[IndexPage] pendingLaunchesAsSets:', pendingLaunchesAsSets)
    
    // Prepare available values for each feature (for "select all" behavior)
    const availableValues: Partial<Record<FeatureKey, Set<string>>> = {}
    if (data) {
      for (const feature of FEATURE_ORDER) {
        const values = data.featureValues[feature]?.map(v => v.value) || []
        if (values.length > 0) {
          availableValues[feature] = new Set(values)
        }
      }
    }
    
    // Prepare launch configuration
    const launchConfig = {
      activeFilters: filters,
      pendingLaunches: pendingLaunchesAsSets,
      availableValues: availableValues, // Add available values for "select all" functionality
      estimatedRuns: estimatedRunCount,
      timestamp: new Date().toISOString()
    }
    
    console.log('[IndexPage] Launch config to pass:', launchConfig)
    
    // Store in sessionStorage for persistence across refresh
    // Convert Sets to arrays for JSON serialization
    const storageConfig = {
      ...launchConfig,
      activeFilters: Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, Array.from(v)])
      ),
      pendingLaunches: Object.fromEntries(
        Object.entries(pendingLaunchesAsSets).map(([k, v]) => [k, Array.from(v)])
      ),
      availableValues: Object.fromEntries(
        Object.entries(availableValues).map(([k, v]) => [k, Array.from(v)])
      )
    }
    sessionStorage.setItem('launchConfig', JSON.stringify(storageConfig))
    console.log('[IndexPage] Saved to sessionStorage:', storageConfig)
    
    // Navigate to launch configuration page
    console.log('[IndexPage] Navigating to /launch with state')
    nav('/launch', { state: launchConfig })
  }

  if (loading) return <div className="app">Loading…</div>
  if (error) return (
    <div className="app">
      <div style={{ color: 'crimson', marginBottom: 16 }}>
        <h2>Error Loading Data</h2>
        <p>{error}</p>
      </div>
      
      {githubStatus && (
        <div style={{ marginBottom: 16, padding: 16, border: '1px solid #ccc', borderRadius: 8 }}>
          <h3>Data Source Debug Info</h3>
          <p><strong>Status:</strong> {githubStatus.accessible ? '✅ Accessible' : '❌ Not Accessible'}</p>
          {githubStatus.source && <p><strong>Data Source:</strong> {githubStatus.source === 'github' ? '🌐 GitHub API' : githubStatus.source === 'local' ? '💾 Local File' : '❌ Neither'}</p>}
          {githubStatus.status && <p><strong>HTTP Status:</strong> {githubStatus.status}</p>}
          {githubStatus.error && <p><strong>Error Details:</strong> {githubStatus.error}</p>}
          {githubStatus.config && (
            <>
              <p><strong>GitHub Token:</strong> {githubStatus.config.tokenConfigured ? '✅ Configured' : '❌ Not Configured'}</p>
              <p><strong>Local Access:</strong> {!githubStatus.config.localDisabled ? '✅ Enabled' : '❌ Disabled'}</p>
            </>
          )}
          
          <h4>Troubleshooting Steps:</h4>
          <ol>
            <li>Check if the repository is public</li>
            <li>Verify the file path exists: <code>data/scratchpad/results_catalog_populated.json</code></li>
            <li>Test GitHub API: <a href="https://api.github.com/repos/TianyiQ/Eval-Reasoning-Consistency/contents/data/scratchpad/results_catalog_populated.json" target="_blank" rel="noopener noreferrer">GitHub API URL</a></li>
            {import.meta.env.VITE_DISABLE_LOCAL !== 'true' && (
              <li>Test local file: <a href="/results_catalog_populated.json" target="_blank" rel="noopener noreferrer">Local File</a></li>
            )}
            <li>Check browser console for detailed error logs</li>
            <li>Verify <code>VITE_GITHUB_TOKEN</code> is set in <code>.env</code></li>
            <li>Check <code>VITE_DISABLE_LOCAL</code> setting in <code>.env</code></li>
          </ol>
        </div>
      )}
    </div>
  )
  if (!data) return null

  return (
    <div className="app">
      <div className="header">
        <div className="title">Truth-Seeking Results Explorer</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className={`btn-toggle ${hideLegacy ? 'active' : ''}`}
            onClick={() => setHideLegacy(!hideLegacy)}
            title="Toggle visibility of legacy, scratchpad, and tmp entries"
          >
            {hideLegacy ? 'Show Hidden' : 'Hide Legacy'}
          </button>
        </div>
      </div>
      <div className="subtext">
        <div>
          Filter by features. Blue = existing results, Pale blue = no results (click to mark for launch), Red = selected for launch.
          {hasGPURequirements && (
            <span style={{ color: '#dc2626', marginLeft: 8 }}>
              ⚠️ GPU-requiring selections detected and will be blocked
            </span>
          )}
        </div>
        {hideLegacy && (
          <div style={{ color: 'var(--text-dim)', marginTop: '6px', fontSize: '13px' }}>
            Hiding entries with legacy/scratchpad/tmp in path.
          </div>
        )}
      </div>

      {FEATURE_ORDER.map((fk) => {
        const pressed = filters[fk] || new Set<string>()
        const pending = pendingLaunches[fk] || []
        const values = data.featureValues[fk]
        
        return (
          <div key={fk} className="row">
            <div className="row-label">{fk}</div>
            <div className="chips">
              {values.map(({ value, count }) => {
                const isPressed = pressed.has(value)
                const isPending = pending.includes(value)
                const wouldActivate = isPressed || wouldButtonActivateAnything(data, filters, fk, value)
                const isGPUBlocked = requiresGPU(fk, value)
                
                // Determine button state and appearance
                let buttonClass = 'chip'
                let buttonTitle = `Count: ${count}`
                let clickHandler = () => toggle(fk, value)
                
                if (isGPUBlocked) {
                  // GPU-blocked buttons are always disabled
                  buttonClass += ' gpu-blocked'
                  buttonTitle = 'Requires GPU (not supported)'
                  clickHandler = () => {} // No-op
                } else if (isPressed) {
                  // Active filter (blue)
                  buttonClass += ' pressed'
                } else if (isPending) {
                  // Pending launch (red)
                  buttonClass += ' pending-launch'
                  buttonTitle += ' (selected for launch)'
                  clickHandler = () => togglePending(fk, value)
                } else if (!wouldActivate) {
                  // No existing results (pale blue, clickable for launch)
                  buttonClass += ' no-results'
                  buttonTitle += ' (no existing results - click to add to launch)'
                  clickHandler = () => togglePending(fk, value)
                } else {
                  // Has results but not selected
                  buttonClass += ' has-results'
                }
                
                return (
                  <button
                    key={value}
                    className={buttonClass}
                    disabled={isGPUBlocked}
                    onClick={clickHandler}
                    title={buttonTitle}
                  >
                    {value}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}

      <div className="statusbar">
        <div className="badge">
          Active Runs: {activeRuns} | Active Artifacts: {activeArtifacts}
          {hasPendingLaunches && (
            <span style={{ marginLeft: 8, color: '#dc2626' }}>
              | Pending Launches: ~{estimatedRunCount} runs
            </span>
          )}
        </div>
        <div className="button-group">
          <button
            className={`btn btn-primary ${hasPendingLaunches ? 'btn-disabled' : ''}`}
            disabled={activeIndices.length === 0 || hasPendingLaunches}
            onClick={() => nav({ pathname: '/explore', search })}
            title={hasPendingLaunches ? 'Clear pending launches to explore results' : 'Explore existing results'}
          >
            Explore Results
          </button>
          <button
            className={`btn btn-launch ${!hasPendingLaunches && (!filters || Object.keys(filters).length === 0) ? 'btn-disabled' : ''}`}
            disabled={hasGPURequirements || (!hasPendingLaunches && (!filters || Object.keys(filters).length === 0))}
            onClick={handleLaunchRuns}
            title={
              hasGPURequirements 
                ? 'GPU-requiring configurations are not supported' 
                : 'Configure and launch new runs'
            }
          >
            Launch Runs
          </button>
        </div>
      </div>
    </div>
  )
}